import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import Breadcrumb from "./PageComponent/Breadcrum";
import FilterHeader from "./PageComponent/FilterHeader";
import UpgradePopup from "./PageComponent/UpgradePopup";

const ITEMS_PER_PAGE = 10;
const MAX_PAGES_TO_SHOW = 7;
const LOCKED_TITLE = "Premium question";
const DEFAULT_FILTER_VALUES = {
  "practice-status": "all",
  "order-by": "all",
  badge: "all",
  complexity: "all",
  pagination: String(ITEMS_PER_PAGE),
};

const getPracticePath = (heading = "") =>
  heading.toLowerCase().replace(/\s/g, "");

const getPageSize = (value, fallback = ITEMS_PER_PAGE) => {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : fallback;
};

const hasUpgradeOnlyRole = (role) => {
  if (Array.isArray(role)) {
    return role.includes(7) || role.includes("7");
  }

  return Number(role) === 7;
};

const PageButton = ({ children, active = false, disabled = false, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex min-h-9 min-w-9 items-center justify-center rounded-md border px-2.5 text-sm font-semibold transition sm:px-3 ${
      active
        ? "border-transparent bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 text-white shadow-sm"
        : "border-slate-200 bg-white text-gray-700 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700"
    } disabled:cursor-not-allowed disabled:opacity-45`}
  >
    {children}
  </button>
);

const StatusPill = ({ children, className = "" }) => (
  <span
    className={`inline-flex h-8 w-fit items-center justify-center whitespace-nowrap rounded-md px-3 text-xs font-bold leading-none sm:text-sm ${className}`}
  >
    {children}
  </span>
);

function QuestionRow({
  title,
  number,
  isLocked,
  isAttempted,
  questionId,
  sectionPath,
  complexityLabel = "Medium",
  badges = [],
}) {
  const displayTitle = isLocked ? LOCKED_TITLE : title;
  const attemptedLabel = isAttempted ? "Attempted" : "Not Attempted";
  const hasNewBadge = badges.includes("new");

  return (
    <article
      className={`rounded-md border border-slate-200 bg-white p-3 shadow-sm transition hover:border-purple-100 hover:shadow-md sm:p-4 ${
        isLocked ? "select-none" : ""
      }`}
    >
      <div className="grid gap-3 xl:grid-cols-[auto,minmax(12rem,1fr),7.5rem,10rem,10rem] xl:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <span className="relative grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-100 text-sm font-extrabold text-gray-700 sm:h-11 sm:w-11">
            {number}
            {hasNewBadge && (
              <span className="absolute -right-2 -top-2 rounded bg-emerald-600 px-1.5 py-0.5 text-[9px] font-extrabold leading-none text-white shadow-sm ring-2 ring-white">
                NEW
              </span>
            )}
          </span>

          <h2
            className={`min-w-0 truncate text-base font-bold text-gray-800 sm:text-lg xl:hidden ${
              isLocked ? "blur-sm" : ""
            }`}
          >
            {displayTitle}
          </h2>
        </div>

        <h2
          className={`hidden min-w-0 truncate text-base font-bold text-gray-800 sm:text-lg xl:block ${
            isLocked ? "blur-sm" : ""
          }`}
        >
          {displayTitle}
        </h2>

        <div className="flex flex-nowrap items-center gap-2 xl:contents">
          <StatusPill
            className={
              isLocked
                ? "bg-gray-200 text-gray-500"
                : "bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 text-white"
            }
          >
            {isLocked ? "Locked" : complexityLabel}
          </StatusPill>

          <StatusPill
            className={
              isAttempted
                ? "bg-green-50 text-green-700 ring-1 ring-green-100"
                : "bg-slate-50 text-gray-500 ring-1 ring-slate-200"
            }
          >
            {attemptedLabel}
          </StatusPill>
        </div>

        <div className="xl:justify-self-end">
          {isLocked ? (
            <span className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-gray-400 sm:w-auto">
              <Lock className="h-4 w-4" aria-hidden="true" />
              Locked
            </span>
          ) : (
            <Link
              className="inline-flex min-h-10 w-full items-center justify-center rounded-md bg-white px-4 text-sm font-bold text-gray-800 underline transition hover:text-fuchsia-800 sm:w-auto"
              to={`/practice/${sectionPath}/${encodeURIComponent(questionId)}`}
            >
              Open Question
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

export default function PageNavigation(props) {
  const initialQuestions = props.id || [];
  const heading = props.heading || "Questions";
  const questionTitle = props.questionTitle;
  const usesServerPagination = Boolean(questionTitle);
  const sectionPath = getPracticePath(heading);
  const [userRole, setUserRole] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState(DEFAULT_FILTER_VALUES);
  const [filterPreferencesLoaded, setFilterPreferencesLoaded] = useState(false);
  const [remoteQuestions, setRemoteQuestions] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({
    page: 1,
    limit: ITEMS_PER_PAGE,
    totalItems: initialQuestions.length,
    totalPages: Math.max(1, Math.ceil(initialQuestions.length / ITEMS_PER_PAGE)),
  });
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(
    usesServerPagination
  );
  const [questionError, setQuestionError] = useState("");
  const lastScrollTop = useRef(0);

  useEffect(() => {
    setCurrentPage(1);
  }, [questionTitle]);

  useEffect(() => {
    const controller = new AbortController();

    const loadFilterPreferences = async () => {
      try {
        const response = await fetch("/api/user-preferences", {
          credentials: "include",
          signal: controller.signal,
        });

        if (response.ok) {
          const data = await response.json();
          const savedFilters = data.practiceFilters || {};

          setFilterValues({
            ...DEFAULT_FILTER_VALUES,
            "practice-status": savedFilters.practiceStatus || "all",
            "order-by": savedFilters.orderBy || "all",
            badge: savedFilters.badge || "all",
            complexity: savedFilters.complexity || "all",
            pagination: savedFilters.pagination || String(ITEMS_PER_PAGE),
          });
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Failed to load practice filter preferences:", error);
        }
      } finally {
        if (!controller.signal.aborted) setFilterPreferencesLoaded(true);
      }
    };

    loadFilterPreferences();

    return () => controller.abort();
  }, []);

  const handleFilterChange = (name, value) => {
    const nextValues = {
      ...filterValues,
      [name]: value,
    };

    setFilterValues(nextValues);
    fetch("/api/user-preferences", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        practiceFilters: {
          practiceStatus: nextValues["practice-status"],
          orderBy: nextValues["order-by"],
          badge: nextValues.badge,
          complexity: nextValues.complexity,
          pagination: nextValues.pagination,
        },
      }),
    }).catch(() => {
      console.error("Failed to save practice filter preferences");
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await fetch("/api/check-auth");
        const data = await response.json();

        if (data.success) {
          setUserRole(data.roles);
        }
      } catch (error) {
        console.error("Error checking user role:", error);
      }
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    if (!usesServerPagination || !filterPreferencesLoaded) return undefined;

    const controller = new AbortController();

    const fetchQuestions = async () => {
      try {
        setIsLoadingQuestions(true);
        setQuestionError("");

        const queryParams = new URLSearchParams({
          page: String(currentPage),
          limit: filterValues.pagination,
        });

        if (filterValues["practice-status"] !== "all") {
          queryParams.set("practiceStatus", filterValues["practice-status"]);
        }

        if (filterValues["order-by"] !== "all") {
          queryParams.set("orderBy", filterValues["order-by"]);
        }

        if (filterValues.badge !== "all") {
          queryParams.set("badge", filterValues.badge);
        }

        if (filterValues.complexity !== "all") {
          queryParams.set("complexity", filterValues.complexity);
        }

        const response = await fetch(
          `/api/questions/${questionTitle}?${queryParams.toString()}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch questions. Status: ${response.status}`);
        }

        const payload = await response.json();
        const nextQuestions = Array.isArray(payload)
          ? payload
          : payload.data || [];
        const nextPagination = payload.pagination || {};
        const totalItems = Number.isFinite(nextPagination.totalItems)
          ? nextPagination.totalItems
          : nextQuestions.length;
        const responseLimit = nextPagination.limit || getPageSize(filterValues.pagination);

        setRemoteQuestions(nextQuestions);
        setPaginationMeta({
          page: nextPagination.page || currentPage,
          limit: responseLimit,
          totalItems,
          totalPages:
            nextPagination.totalPages ||
            Math.max(1, Math.ceil(totalItems / responseLimit)),
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching questions:", error);
          setRemoteQuestions([]);
          setQuestionError("Failed to load questions. Please try again.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingQuestions(false);
        }
      }
    };

    fetchQuestions();

    return () => {
      controller.abort();
    };
  }, [
    currentPage,
    filterValues["practice-status"],
    filterValues["order-by"],
    filterValues.badge,
    filterValues.complexity,
    filterValues.pagination,
    questionTitle,
    filterPreferencesLoaded,
    usesServerPagination,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;

      if (scrollTop > 80 && scrollTop >= lastScrollTop.current) {
        setShowPopup(true);
      } else if (scrollTop < 40) {
        setShowPopup(false);
      }

      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const qData = usesServerPagination ? remoteQuestions : initialQuestions;
  const totalItems = usesServerPagination
    ? paginationMeta.totalItems
    : qData.length;
  const localPageSize =
    filterValues.pagination === "all"
      ? Math.max(qData.length, 1)
      : getPageSize(filterValues.pagination);
  const activePageSize = usesServerPagination
    ? paginationMeta.limit || ITEMS_PER_PAGE
    : localPageSize;
  const totalPages = usesServerPagination
    ? paginationMeta.totalPages
    : Math.max(1, Math.ceil(qData.length / activePageSize));
  const startIndex = (currentPage - 1) * activePageSize;
  const visibleQuestions = usesServerPagination
    ? qData
    : qData.slice(startIndex, Math.min(startIndex + activePageSize, qData.length));
  const halfMaxPages = Math.floor(MAX_PAGES_TO_SHOW / 2);
  const shouldShowUpgradePrompt = hasUpgradeOnlyRole(userRole);
  const hasLockedQuestions = shouldShowUpgradePrompt && totalItems > 5;

  useEffect(() => {
    if (hasLockedQuestions) {
      setShowPopup(true);
    }
  }, [hasLockedQuestions]);

  const pageNumbers = useMemo(() => {
    const numbers = [];

    if (totalPages <= MAX_PAGES_TO_SHOW) {
      for (let page = 1; page <= totalPages; page += 1) numbers.push(page);
    } else if (currentPage <= halfMaxPages) {
      for (let page = 1; page <= MAX_PAGES_TO_SHOW; page += 1) {
        numbers.push(page);
      }
    } else if (currentPage > totalPages - halfMaxPages) {
      for (
        let page = totalPages - MAX_PAGES_TO_SHOW + 1;
        page <= totalPages;
        page += 1
      ) {
        numbers.push(page);
      }
    } else {
      for (
        let page = currentPage - halfMaxPages;
        page <= currentPage + halfMaxPages;
        page += 1
      ) {
        numbers.push(page);
      }
    }

    return numbers;
  }, [currentPage, halfMaxPages, totalPages]);

  const handlePageClick = (page) => {
    if (shouldShowUpgradePrompt) return;
    if (isLoadingQuestions) return;
    setCurrentPage(page);
  };

  return (
    <main className="min-h-screen bg-slate-50 pt-24 sm:pl-72">
      <section className="w-full overflow-hidden px-4 pb-10 sm:px-6 lg:px-8">
        <div className="rounded-md bg-gray-100 p-3 sm:p-5 lg:p-6">
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase text-purple-700">
                Practice Questions
              </p>
              <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
                {heading}
              </h1>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
              <div className="rounded-md bg-slate-50 px-3 py-3 sm:px-4">
                <p className="text-xs font-semibold uppercase text-gray-500">
                  Questions
                </p>
                <p className="text-xl font-extrabold text-gray-900">
                  {totalItems}
                </p>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-3 sm:px-4">
                <p className="text-xs font-semibold uppercase text-gray-500">
                  Page
                </p>
                <p className="text-xl font-extrabold text-gray-900">
                  {currentPage}/{totalPages}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <Breadcrumb />
          </div>

          <div className="mt-5">
            <FilterHeader
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
            />
          </div>
        </div>

        <section className="mt-6 space-y-3">
          {isLoadingQuestions && (
            <div className="rounded-md border border-slate-200 bg-white p-8 text-center shadow-sm">
              <p className="text-sm font-semibold uppercase text-purple-700">
                Loading questions
              </p>
              <p className="mt-2 text-gray-500">
                Fetching this page of practice questions.
              </p>
            </div>
          )}

          {!isLoadingQuestions && questionError && (
            <div className="rounded-md border border-red-100 bg-white p-8 text-center shadow-sm">
              <p className="text-sm font-semibold uppercase text-red-600">
                Unable to load
              </p>
              <p className="mt-2 text-gray-500">{questionError}</p>
            </div>
          )}

          {!isLoadingQuestions && !questionError && visibleQuestions.map((item, index) => (
            <QuestionRow
              key={item._id}
              number={startIndex + index + 1}
              title={item.q_title}
              questionId={item._id}
              sectionPath={sectionPath}
              complexityLabel={item.complexityLabel}
              badges={item.badges || []}
              isAttempted={Boolean(item.isAttempted || item.attempted)}
              isLocked={shouldShowUpgradePrompt && startIndex + index >= 5}
            />
          ))}

          {!isLoadingQuestions && !questionError && visibleQuestions.length === 0 && (
            <div className="rounded-md border border-slate-200 bg-white p-8 text-center shadow-sm">
              <p className="text-sm font-semibold uppercase text-purple-700">
                No questions
              </p>
              <p className="mt-2 text-gray-500">
                Questions will appear here when they are available.
              </p>
            </div>
          )}
        </section>

        <nav
          aria-label="Pagination"
          className={`mt-6 rounded-md border border-slate-200 bg-white p-3 shadow-sm ${
            shouldShowUpgradePrompt ? "blur-sm" : ""
          }`}
        >
          <ol className="flex flex-wrap justify-center gap-2">
            <li>
              <PageButton
                disabled={currentPage === 1 || shouldShowUpgradePrompt || isLoadingQuestions}
                onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
              >
                <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
                Prev
              </PageButton>
            </li>

            {pageNumbers.map((number) => (
              <li key={number}>
                <PageButton
                  active={number === currentPage}
                  disabled={shouldShowUpgradePrompt || isLoadingQuestions}
                  onClick={() => handlePageClick(number)}
                >
                  {number}
                </PageButton>
              </li>
            ))}

            <li>
              <PageButton
                disabled={currentPage === totalPages || shouldShowUpgradePrompt || isLoadingQuestions}
                onClick={() =>
                  handlePageClick(Math.min(currentPage + 1, totalPages))
                }
              >
                Next
                <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </PageButton>
            </li>
          </ol>
        </nav>
        </div>
      </section>

      {showPopup && shouldShowUpgradePrompt && <UpgradePopup />}
    </main>
  );
}
